import { SubHeading, SummaryCard } from "@certn/cascadia-react";
import { Link } from "@certn/cascadia";
import { CascadiaJsonFormContext } from "@certn/forms";
import { SummaryItem } from "@certn/forms/src/components/SummaryItem";
import { Fragment, useContext } from "react";
import { TFunction } from "i18next";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import { FITNESS_PROPRIETY_KEY } from "./types";
import { FitnessProprietyLayoutContext } from "../../layouts/FitnessProprietyLayoutContext";
import { useTranslation } from "react-i18next";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { NotStarted } from "../../layouts/NotStarted";

type Claim = components["schemas"]["CustomQuestionnaireTemplateInputClaim"];

// Mirrors the backend's _format_answer helper in
// project/checks/financially_regulated/fitness_propriety/v1/hooks.py so the
// applicant summary card matches the report rendering. Bool radios store
// answers as the strings "true"/"false" (see FitnessField.tsx); str claims
// with selectable_options store the option's `value`, so resolve back to
// the option's `label` for display.
function formatAnswer({
  value,
  claim,
  t,
}: {
  value: string | number | boolean | null | undefined;
  claim: Claim;
  t: TFunction;
}): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (claim.type === "bool") {
    const isTrue = value === true || value === "true";
    const isFalse = value === false || value === "false";
    if (isTrue) return t("Yes");
    if (isFalse) return t("No");
  }

  const selectableOptions = claim.constraints?.[0]?.selectable_options;
  if (selectableOptions && selectableOptions.length > 0) {
    const option = selectableOptions.find((o) => o.value === String(value));
    if (option) return option.label;
  }

  return String(value);
}

export function FitnessProprietySummaryControl() {
  const { t } = useTranslation();
  const { caseId } = useApplicationFormContext();
  const { sectionLabel, templateData } = useContext(
    FitnessProprietyLayoutContext
  );
  const { values } = useContext(CascadiaJsonFormContext);

  const hasRequiredClaims = (templateData?.templates ?? []).some((template) =>
    template.template_input_claims.some((claim) => claim.is_optional === false)
  );

  const groupedAnswers = (templateData?.templates ?? [])
    .map((template) => ({
      templateName: template.name,
      answers: template.template_input_claims
        .filter(
          (claim) =>
            values[FITNESS_PROPRIETY_KEY] &&
            claim.name in values[FITNESS_PROPRIETY_KEY]
        )
        .map((claim) => ({
          name: claim.name,
          label: claim.label,
          value: formatAnswer({
            value: values[FITNESS_PROPRIETY_KEY]?.[claim.name],
            claim,
            t,
          }),
        })),
    }))
    .filter((group) => group.answers.length > 0);

  if (groupedAnswers.length === 0 && hasRequiredClaims) {
    return (
      <SummaryCard
        header={sectionLabel}
        actions={
          <Link
            href={`/apply/${caseId}/fitness-propriety`}
            textColor="text-weak"
            testID="fitness-propriety-edit-button"
          >
            {t("Edit")}
          </Link>
        }
      >
        <NotStarted sectionKey={"fitness-propriety"} showErrors={false} />
      </SummaryCard>
    );
  }

  return (
    <SummaryCard
      header={sectionLabel}
      actions={
        <Link
          href={`/apply/${caseId}/fitness-propriety`}
          textColor="text-weak"
          testID="fitness-propriety-edit-button"
        >
          {t("Edit")}
        </Link>
      }
    >
      {groupedAnswers.map((group) => (
        <Fragment key={group.templateName}>
          <SubHeading>{group.templateName}</SubHeading>
          {group.answers.map((answer) => (
            <SummaryItem key={answer.name} label={answer.label ?? answer.name}>
              {answer.value}
            </SummaryItem>
          ))}
        </Fragment>
      ))}
    </SummaryCard>
  );
}
